import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { useAuth } from "../AuthContext";
import { PLATFORM_META } from "../platforms";
import { Trash2, Plus, KeyRound, Hash, Users, UserCheck } from "lucide-react";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20 focus:border-[#0047AB] disabled:bg-slate-50";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";
const card = "bg-white border border-slate-200 rounded-lg p-5 sm:p-6";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [gstzen, setGstzen] = useState(null);
  const [series, setSeries] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "maker", permissions: { expenses: false, bank: false } });
  const [executives, setExecutives] = useState([]);
  const [newExec, setNewExec] = useState("");

  const loadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  const loadExecs = () => api.get("/executives").then((r) => setExecutives(r.data));

  useEffect(() => {
    api.get("/settings/gstzen").then((r) => setGstzen(r.data));
    api.get("/settings/series").then((r) => setSeries(r.data));
    if (isAdmin) { loadUsers(); loadExecs(); }
  }, [isAdmin]);

  const addExec = async (e) => {
    e.preventDefault();
    try {
      await api.post("/executives", { name: newExec });
      toast.success("Executive added");
      setNewExec("");
      loadExecs();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveGstzen = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/settings/gstzen", { api_token: gstzen.api_token, base_url: gstzen.base_url });
      setGstzen(data);
      toast.success("GSTzen API settings saved");
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveSeries = async () => {
    try {
      const { data } = await api.put("/settings/series", series);
      setSeries(data);
      toast.success("Invoice series saved");
    } catch (err) { toast.error(apiError(err)); }
  };

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", newUser);
      toast.success("User created");
      setNewUser({ name: "", email: "", password: "", role: "maker", permissions: { expenses: false, bank: false } });
      loadUsers();
    } catch (err) { toast.error(apiError(err)); }
  };

  const togglePermission = async (u, key, label) => {
    try {
      const next = { expenses: !!u.permissions?.expenses, bank: !!u.permissions?.bank, [key]: !u.permissions?.[key] };
      await api.patch(`/users/${u.id}/permissions`, { permissions: next });
      toast.success(`${label} ${next[key] ? "enabled" : "disabled"} for ${u.name}`);
      loadUsers();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (!gstzen || !series) return <div className="text-slate-500 text-sm">Loading...</div>;

  return (
    <div data-testid="settings-page" className="max-w-3xl space-y-8">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Configuration</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Settings</h1>
      </div>

      <form onSubmit={saveGstzen} className={card}>
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={16} className="text-[#0047AB]" />
          <h2 className="font-semibold text-slate-900">GSTzen API</h2>
          {gstzen.is_sandbox && <span className="text-[10px] uppercase tracking-wide bg-amber-50 text-amber-800 border border-amber-300 rounded-full px-2 py-0.5 font-semibold">Sandbox</span>}
        </div>
        <p className="text-sm text-slate-500 mb-4">Used for E-Invoice (IRN) and E-Way Bill generation. Replace the sandbox token with your live GSTzen API key when ready.</p>
        <div className="grid gap-4">
          <div>
            <label className={labelCls}>API Token</label>
            <input data-testid="gstzen-token-input" disabled={!isAdmin} value={gstzen.api_token}
              onChange={(e) => setGstzen({ ...gstzen, api_token: e.target.value })} className={`${inputCls} mt-1 font-mono`} />
          </div>
          <div>
            <label className={labelCls}>Base URL</label>
            <input data-testid="gstzen-url-input" disabled={!isAdmin} value={gstzen.base_url}
              onChange={(e) => setGstzen({ ...gstzen, base_url: e.target.value })} className={`${inputCls} mt-1 font-mono`} />
          </div>
          {isAdmin && <div className="flex justify-end"><button data-testid="gstzen-save-btn" className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-200">Save API Settings</button></div>}
        </div>
      </form>

      <div className={card}>
        <div className="flex items-center gap-2 mb-4">
          <Hash size={16} className="text-[#0047AB]" />
          <h2 className="font-semibold text-slate-900">Invoice Number Series</h2>
        </div>
        <div className="space-y-3">
          {Object.entries(PLATFORM_META).map(([k, m]) => (
            <div key={k} className="grid grid-cols-3 gap-3 items-center">
              <div className="text-sm font-medium">{m.name}</div>
              <input data-testid={`series-${k}-prefix-input`} disabled={!isAdmin} value={series[k]?.prefix || ""}
                onChange={(e) => setSeries({ ...series, [k]: { ...series[k], prefix: e.target.value } })}
                className={`${inputCls} font-mono`} placeholder="Prefix" />
              <input data-testid={`series-${k}-next-input`} disabled={!isAdmin} type="number" value={series[k]?.next ?? 1}
                onChange={(e) => setSeries({ ...series, [k]: { ...series[k], next: Number(e.target.value) } })}
                className={`${inputCls} font-mono`} placeholder="Next No." />
            </div>
          ))}
          {isAdmin && <div className="flex justify-end"><button data-testid="series-save-btn" onClick={saveSeries} className="bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-200">Save Series</button></div>}
        </div>
      </div>

      {isAdmin && (
        <div className={card}>
          <div className="flex items-center gap-2 mb-1">
            <UserCheck size={16} className="text-[#0047AB]" />
            <h2 className="font-semibold text-slate-900">Executives</h2>
          </div>
          <p className="text-sm text-slate-500 mb-4">Sales executives selectable on every invoice (compulsory). Not printed on the customer invoice — used for executive-wise sales reports.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {executives.map((x) => (
              <span key={x.id} data-testid={`executive-chip-${x.name}`} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-sm">
                {x.name}
                <button data-testid={`executive-delete-${x.name}`} onClick={async () => { await api.delete(`/executives/${x.id}`); loadExecs(); }}
                  className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={13} /></button>
              </span>
            ))}
            {executives.length === 0 && <span className="text-sm text-slate-400">No executives added yet.</span>}
          </div>
          <form onSubmit={addExec} className="flex gap-3">
            <input data-testid="new-executive-input" required placeholder="Executive name" value={newExec}
              onChange={(e) => setNewExec(e.target.value)} className={inputCls} />
            <button data-testid="new-executive-add-btn" className="inline-flex items-center gap-1 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 shrink-0">
              <Plus size={14} /> Add
            </button>
          </form>
        </div>
      )}

      {isAdmin && (
        <div className={card}>
          <div className="flex items-center gap-2 mb-4">
            <Users size={16} className="text-[#0047AB]" />
            <h2 className="font-semibold text-slate-900">Users</h2>
          </div>
          <div className="divide-y divide-slate-100 mb-5">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1">
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5 font-semibold">{u.role}</span>
                {u.role !== "admin" && (
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                    <input data-testid={`user-expenses-toggle-${u.email}`} type="checkbox"
                      checked={!!u.permissions?.expenses} onChange={() => togglePermission(u, "expenses", "Expense Manager")} />
                    Expenses
                  </label>
                )}
                {u.role !== "admin" && (
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                    <input data-testid={`user-bank-toggle-${u.email}`} type="checkbox"
                      checked={!!u.permissions?.bank} onChange={() => togglePermission(u, "bank", "Bank Statements")} />
                    Bank
                  </label>
                )}
                {u.id !== user.id && (
                  <button data-testid={`user-delete-${u.email}`} onClick={async () => { await api.delete(`/users/${u.id}`); api.get("/users").then((r) => setUsers(r.data)); }}
                    className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={addUser} className="grid sm:grid-cols-2 gap-3">
            <input data-testid="new-user-name-input" required placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className={inputCls} />
            <input data-testid="new-user-email-input" required type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={inputCls} />
            <input data-testid="new-user-password-input" required type="password" placeholder="Password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={inputCls} />
            <div className="flex gap-3 items-center">
              <select data-testid="new-user-role-select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className={inputCls}>
                <option value="maker">Maker</option><option value="checker">Checker</option><option value="admin">Admin</option>
              </select>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                <input data-testid="new-user-expenses-checkbox" type="checkbox" checked={newUser.permissions.expenses}
                  onChange={(e) => setNewUser({ ...newUser, permissions: { ...newUser.permissions, expenses: e.target.checked } })} />
                Expenses
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600 whitespace-nowrap cursor-pointer">
                <input data-testid="new-user-bank-checkbox" type="checkbox" checked={newUser.permissions.bank}
                  onChange={(e) => setNewUser({ ...newUser, permissions: { ...newUser.permissions, bank: e.target.checked } })} />
                Bank
              </label>
              <button data-testid="new-user-save-btn" className="inline-flex items-center gap-1 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 shrink-0">
                <Plus size={14} /> Add
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
